/**
 * Intelligence → Insights: the persisted findings surface.
 *
 * Function names and paths mirror the route inventory verbatim
 * (`apps/api/src/domains/intelligence/findings/routes.ts`).
 *
 * Re-exported from `index.ts` as `export * as insights from './insights.js';`,
 * so the types arrive as members of that namespace rather than as siblings of
 * it. `apps/admin/src/features/insights/insightsClient.ts` is now a thin
 * re-export of the same names.
 *
 * ---------------------------------------------------------------------------
 * FOUR-STATE DISCIPLINE ON THE WIRE
 * ---------------------------------------------------------------------------
 * `FindingEntity.disclosure` is a discriminated union, not a nullable label:
 *
 *   shown     the caller may read this entity; `label` is its name.
 *   withheld  it exists and policy forbids naming it here.
 *   absent    the row it named is gone.
 *
 * A caller who reaches none of a finding's SUBJECT entities never receives the
 * finding at all, so `refused-for-scope` is answered one level up — by the
 * list's `organizationWide` flag and by a 404 from the detail route.
 */
import { get, post } from './transport.js';
function repeatable(name, value) {
    if (value === undefined)
        return '';
    const values = Array.isArray(value) ? value : [value];
    return values.map((entry) => `${name}=${encodeURIComponent(entry)}`).join('&');
}
/** `GET /api/insights/findings` — the findings you reach (needs `insights:read`). */
export async function list(query = {}, options) {
    // Built by hand rather than through `QueryParams`, because every filter here
    // is repeatable and `URLSearchParams.append` over a record cannot express
    // `state=needs_attention&state=watching`.
    const parts = [
        repeatable('state', query.state),
        repeatable('severity', query.severity),
        repeatable('programId', query.programId),
        repeatable('locationId', query.locationId),
        repeatable('ownerUserId', query.ownerUserId),
        repeatable('ownerKind', query.ownerKind),
        repeatable('ownerRoleId', query.ownerRoleId),
        repeatable('findingType', query.findingType),
        repeatable('workerId', query.workerId),
        repeatable('clientId', query.clientId),
        query.since ? `since=${encodeURIComponent(query.since)}` : '',
        query.limit === undefined ? '' : `limit=${query.limit}`,
        query.offset === undefined ? '' : `offset=${query.offset}`,
    ].filter((part) => part !== '');
    const suffix = parts.length > 0 ? `?${parts.join('&')}` : '';
    return get(`/api/insights/findings${suffix}`, options);
}
/** `GET /api/insights/findings/:id` — 404 for a finding you do not reach. */
export async function getFinding(findingId, options) {
    const body = await get(`/api/insights/findings/${encodeURIComponent(findingId)}`, options);
    return body.finding;
}
/** `POST .../acknowledge` — needs_attention → watching (needs `insights:triage`). */
export async function acknowledge(findingId, options) {
    const body = await post(`/api/insights/findings/${encodeURIComponent(findingId)}/acknowledge`, {}, options);
    return body.finding;
}
/**
 * `POST .../dismiss` — with a reason (needs `insights:triage`).
 *
 * The reason is required by the server, by a CHECK constraint, and by this
 * signature. It is written to an append-only event and is the only place it
 * lives, so a second dismissal can never overwrite the first.
 */
export async function dismiss(findingId, reason, options) {
    const body = await post(`/api/insights/findings/${encodeURIComponent(findingId)}/dismiss`, { reason }, options);
    return body.finding;
}
/**
 * `GET .../owner-suggestions` — who the ORGANIZATION'S OWN RECORDS say is
 * responsible (needs `insights:read`).
 *
 * A READ. It charges the read key, not the triage key, because looking at who
 * would be asked is reading; and nothing it returns can be redeemed for an
 * assignment. To act on a suggestion, call `assignOwner` naming the owner.
 */
export async function ownerSuggestions(findingId, options) {
    return get(`/api/insights/findings/${encodeURIComponent(findingId)}/owner-suggestions`, options);
}
/**
 * `POST .../owner` — assign or reassign the owner (needs `insights:triage`).
 *
 * The owner is named EXPLICITLY. There is no "accept the suggestion" call:
 * `acceptedSuggestionFrom` only RECORDS that the person got the idea from a
 * relation the server derived, and dropping it would change the history and
 * never the owner written.
 *
 * The server additionally requires that the caller reach the finding and reach
 * the person they are naming; both refusals arrive as 403 with a message, and
 * "no such account" is deliberately indistinguishable from "an account you may
 * not see".
 */
export async function assignOwner(findingId, owner, extra, options) {
    const body = await post(`/api/insights/findings/${encodeURIComponent(findingId)}/owner`, {
        owner,
        acceptedSuggestionFrom: extra?.acceptedSuggestionFrom ?? null,
        reason: extra?.reason ?? null,
    }, options);
    return body.finding;
}
// ---------------------------------------------------------------------------
// Applicability
// ---------------------------------------------------------------------------
/**
 * `GET /api/insights/applicability` — the determinations this organization has
 * recorded (needs `insights:read`).
 *
 * A READ, on the read key, deliberately: seeing WHY a finding is silent is
 * part of reading the queue. A surface that could show "not applicable"
 * without showing the determination behind it would be an unexplained
 * suppression, which is the exact shape this whole mechanism exists to make
 * impossible.
 */
export async function listApplicability(query, options) {
    const parts = [
        query?.findingType === undefined ? '' : `findingType=${encodeURIComponent(query.findingType)}`,
        query?.includeEnded === undefined
            ? ''
            : `includeEnded=${query.includeEnded ? 'true' : 'false'}`,
    ].filter((part) => part.length > 0);
    return get(`/api/insights/applicability${parts.length > 0 ? `?${parts.join('&')}` : ''}`, options);
}
/**
 * `POST /api/insights/applicability` — record a determination (needs
 * `insights:applicability`).
 *
 * THE KEY IS A REAL CAPABILITY SINCE FOUNDER CLOSEOUT RULING 5 OF 2026-09-08,
 * seeded to `org_admin` and grantable to explicitly designated
 * compliance/authority roles. It was `reserved` and conferrable on nobody
 * until then; `2032000000000` §6 records why, and `2200000000000` is the
 * release.
 *
 * THREE WAYS THIS IS REFUSED, AND THEY ARE DIFFERENT ANSWERS.
 *
 *   403 with `permission` — the caller does not hold the key.
 *
 *   403 with `refusal` — the caller holds the key and the ACT is not theirs to
 *   perform. `platform_safety_control`: a suppressing ruling against Phibian's
 *   own execution envelope, which has a separate resolution authority.
 *   `governing_effective_window`: `not_yet_effective` or `historical` against
 *   a `regulatory` or `contractual` requirement, because whether a law is in
 *   force is established by the source's effective dates and is not an
 *   applicability judgement. No grant changes either answer.
 *
 *   400 — the judgement is malformed. A suppressing disposition
 *   (`does_not_apply`, `not_yet_effective`, `historical`) needs a program or
 *   location scope AND a `basisSource`.
 *
 * There is no `decidedBy` field; the decider is the session.
 */
export async function recordApplicability(input, options) {
    return post('/api/insights/applicability', input, options);
}
/**
 * `POST /api/insights/applicability/:id/end` — end one (needs
 * `insights:applicability`).
 *
 * Ending is a second recorded act, never an erasure, and it brings back in the
 * same transaction every finding it was silencing that nothing else silences.
 */
export async function endApplicability(determinationId, reason, options) {
    return post(`/api/insights/applicability/${encodeURIComponent(determinationId)}/end`, { reason }, options);
}
//# sourceMappingURL=insights.js.map